/**
 * Tableau de bord — cockpit quotidien du magasin.
 * KPI du jour (vs veille), alertes opérationnelles, CA 7 jours, top produits,
 * dernières ventes et accès rapides. L'analyse approfondie reste dans /analytics.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import api from '../services/api';
import ChartCard from '../components/analytics/ChartCard';

interface AlertCounts {
  total: number;
  shelf_empty: number;
  shelf_low: number;
  order_suggested: number;
  withdrawn: number;
}

/**
 * Relevé des paniers partis au paiement et jamais revenus (une ligne = un
 * client). Les types suivent la réponse de `GET /orders/admin/checkout-drafts`
 * — voir `checkout-draft.service.ts` pour ce que chaque champ signifie.
 */
interface CommandeSuivante {
  order_number: string;
  created_at: string;
  total_cents: number;
  payment_status: string;
  delai_minutes: number;
}

interface PanierAbandonne {
  id: string;
  customer_id: string;
  /** Horodatage UTC : formaté en heure belge à l'affichage. */
  created_at: string;
  status: string;
  nb_tentatives: number;
  client: { nom: string; email: string | null; telephone: string | null };
  montant_cents: number;
  nb_lignes: number;
  date_retrait: string | null;
  commande_suivante: CommandeSuivante | null;
  /** De l'argent a été encaissé sans qu'aucune commande n'existe derrière. */
  argent_pris: boolean;
  argent_encaisse_cents: number;
}

interface RelevePaniersAbandonnes {
  total: number;
  incidents: number;
  incidents_cents: number;
  abandons: number;
  abandons_cents: number;
  paniers: PanierAbandonne[];
}

const eur = (v: number) =>
  v.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });

const centsToEur = (cents: number) => eur((cents || 0) / 100);

/**
 * La base stocke de l'UTC et Prisma sérialise en ISO « …Z » : `format()` de
 * date-fns prendrait le fuseau du NAVIGATEUR — juste sur le Mac mini du
 * magasin, faux dès qu'on regarde le tableau de bord depuis un iPhone en
 * vacances. L'heure d'un panier abandonné sert à rappeler quelqu'un : elle doit
 * être l'heure belge, toujours.
 */
const heureBelge = (iso: string) =>
  new Date(iso).toLocaleString('fr-BE', {
    timeZone: 'Europe/Brussels',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * « il y a 5 h » plutôt qu'un nombre de minutes à quatre chiffres. Le serveur
 * sait exactement quand la commande est arrivée, même douze heures plus tard :
 * l'écran doit dire ce qu'il sait, jamais affirmer qu'il ne s'est rien passé.
 */
const delaiLisible = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 60 * 48) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (60 * 24))} j`;
};

/** « 2026-09-11 » n'est pas une date qu'on lit au vol : le retrait prévu dit s'il est encore temps. */
const dateCourte = (jour: string) => `${jour.slice(8, 10)}/${jour.slice(5, 7)}`;

/** Ce que la carte annonce en sous-titre — deux situations d'argent, jamais mélangées. */
const resumeAbandons = (r: RelevePaniersAbandonnes) => {
  const parts: string[] = [];
  if (r.incidents > 0) {
    parts.push(
      `${r.incidents} encaissé${r.incidents > 1 ? 's' : ''} sans commande (${centsToEur(
        r.incidents_cents,
      )})`,
    );
  }
  if (r.abandons > 0) {
    parts.push(
      `${r.abandons} panier${r.abandons > 1 ? 's' : ''} sans suite (${centsToEur(
        r.abandons_cents,
      )} jamais encaissés)`,
    );
  }
  // Les clients rattrapés sont affichés (ils expliquent la ligne) : les taire
  // dans le sous-titre ferait compter à Philippe plus de problèmes qu'il n'y en
  // a — une carte « 1 panier sans suite » au-dessus de sept lignes.
  const rattrapes = r.total - r.incidents - r.abandons;
  if (rattrapes > 0) {
    parts.push(`${rattrapes} rattrapé${rattrapes > 1 ? 's' : ''} par une commande`);
  }
  parts.push('7 derniers jours');
  return parts.join(' · ');
};

function Variation({ percent }: { percent?: number }) {
  if (percent === undefined || percent === null || !isFinite(percent)) return null;
  const up = percent >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        up ? 'text-emerald-600' : 'text-red-600'
      }`}
    >
      {up ? '▲' : '▼'} {Math.abs(percent).toFixed(0)}%
      <span className="text-gray-400 font-normal">vs veille</span>
    </span>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState<any>(null);
  const [byDay, setByDay] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any>(null);
  const [restock, setRestock] = useState<AlertCounts | null>(null);
  const [kpis, setKpis] = useState<any>(null);
  const [paniersAbandonnes, setPaniersAbandonnes] =
    useState<RelevePaniersAbandonnes | null>(null);
  const [releveIndisponible, setReleveIndisponible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const weekAgo = format(subDays(new Date(), 6), 'yyyy-MM-dd');
    const results = await Promise.allSettled([
      api.getSalesOverview(today, today),
      api.getSalesByPeriod(weekAgo, today, 'day'),
      api.getTopProducts(today, today, 5),
      api.getInventoryOverview(),
      api.get<AlertCounts>('/pos/restock-alerts/count'),
      api.getOperationalKPIs(),
      // Par le client axios de l'application, jamais par un `fetch` maison : lui
      // seul choisit le jeton STAFF hors de `/shop`. Un `customer_token` qui
      // traîne dans le même navigateur (l'eshop testé depuis le bureau) partirait
      // sinon en tête, l'API répondrait 401 et `allSettled` avalerait la carte
      // sans le moindre indice à l'écran.
      api.get<RelevePaniersAbandonnes>('/orders/admin/checkout-drafts?jours=7'),
    ]);
    const val = (i: number) =>
      results[i].status === 'fulfilled' ? (results[i] as any).value : null;
    // Pour les six premières cartes, échouer en silence coûte un graphique. Pour
    // le relevé des paniers, c'est le SEUL canal visuel d'un encaissement sans
    // commande : un écran muet se lit « rien à signaler ». On distingue donc
    // l'échec de l'absence d'incident.
    setReleveIndisponible(results[6].status === 'rejected');
    setOverview(val(0));
    // Combler les jours sans vente (absents du GROUP BY) par des zéros
    const raw: any[] = val(1) || [];
    const filled = Array.from({ length: 7 }, (_, i) => {
      const d = format(subDays(new Date(), 6 - i), 'yyyy-MM-dd');
      const hit = raw.find((r) => format(new Date(r.period), 'yyyy-MM-dd') === d);
      return hit || { period: d, ca_eur: 0, transaction_count: 0 };
    });
    setByDay(filled);
    setTopProducts(val(2) || []);
    setInventory(val(3));
    setRestock(val(4));
    setKpis(val(5));
    setPaniersAbandonnes(val(6));
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const restockPending = restock
    ? restock.shelf_empty + restock.shelf_low + restock.order_suggested
    : 0;

  const alerts = [
    restockPending > 0 && {
      to: '/pos/restock-alerts',
      icon: '🔔',
      color: 'bg-amber-50 border-amber-300 hover:bg-amber-100',
      label: `${restockPending} alerte${restockPending > 1 ? 's' : ''} de réappro`,
      detail: `${restock!.shelf_empty} rayon vide · ${restock!.shelf_low} rayon bas · ${restock!.order_suggested} à commander`,
    },
    restock && restock.withdrawn > 0 && {
      to: '/pos/restock-alerts',
      icon: '🗂️',
      color: 'bg-gray-50 border-gray-300 hover:bg-gray-100',
      label: `${restock.withdrawn} produit${restock.withdrawn > 1 ? 's' : ''} retiré${restock.withdrawn > 1 ? 's' : ''} avec stock`,
      detail: 'À vérifier (périmés / désactivés)',
    },
    inventory && inventory.expiring_soon > 0 && {
      to: '/analytics',
      icon: '⏳',
      color: 'bg-orange-50 border-orange-300 hover:bg-orange-100',
      label: `${inventory.expiring_soon} lot${inventory.expiring_soon > 1 ? 's' : ''} à DLC proche`,
      detail: 'Expire sous 7 jours',
    },
  ].filter(Boolean) as {
    to: string;
    icon: string;
    color: string;
    label: string;
    detail: string;
  }[];

  const quickLinks = [
    { to: '/pos', icon: '🛒', label: 'Caisse', color: 'bg-blue-50 hover:bg-blue-100' },
    { to: '/pos/reception', icon: '📥', label: 'Réception', color: 'bg-teal-50 hover:bg-teal-100' },
    { to: '/pos/restock-alerts', icon: '🔔', label: 'Réappro', color: 'bg-amber-50 hover:bg-amber-100' },
    { to: '/inventory', icon: '📋', label: 'Inventaire', color: 'bg-purple-50 hover:bg-purple-100' },
    { to: '/closing-reports', icon: '🧾', label: 'Rapports Z', color: 'bg-rose-50 hover:bg-rose-100' },
    { to: '/products', icon: '📦', label: 'Produits', color: 'bg-indigo-50 hover:bg-indigo-100' },
    { to: '/customers', icon: '👥', label: 'Clients', color: 'bg-orange-50 hover:bg-orange-100' },
    { to: '/analytics', icon: '📈', label: 'Analytics', color: 'bg-emerald-50 hover:bg-emerald-100' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="mt-1 text-sm text-gray-500 capitalize">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </p>
      </div>

      {/* KPI du jour */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm font-medium text-gray-600">CA du jour (TTC)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {centsToEur(overview?.total_ca_cents)}
          </p>
          <Variation percent={overview?.comparison?.ca_variation_percent} />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm font-medium text-gray-600">Ventes</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {overview?.transaction_count ?? 0}
          </p>
          <Variation percent={overview?.comparison?.transaction_variation_percent} />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm font-medium text-gray-600">Panier moyen</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {centsToEur(overview?.average_basket_cents)}
          </p>
          <Variation percent={overview?.comparison?.basket_variation_percent} />
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <p className="text-sm font-medium text-gray-600">Marge du jour</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {centsToEur(overview?.margin_cents)}
          </p>
          {overview?.margin_percent > 0 && (
            <span className="text-xs text-gray-400">
              {overview.margin_percent.toFixed(1)}% (périmètre à coût connu)
            </span>
          )}
        </div>
      </div>

      {/* Alertes opérationnelles */}
      {alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {alerts.map((a) => (
            <Link
              key={a.label}
              to={a.to}
              className={`flex items-center gap-3 rounded-lg border-2 p-4 transition-colors ${a.color}`}
            >
              <span className="text-3xl">{a.icon}</span>
              <div>
                <p className="font-semibold text-gray-900">{a.label}</p>
                <p className="text-xs text-gray-600">{a.detail}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Le relevé n'a pas pu être lu : le dire. Sans cette ligne, un incident
          d'argent et une API en panne donnent exactement le même écran. */}
      {releveIndisponible && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          Relevé des paniers abandonnés indisponible — réessaie en rechargeant la page.
        </div>
      )}

      {/* Paniers partis au paiement et jamais revenus. Rien à l'écran quand tout
          a été rattrapé : une carte qui annonce zéro use le regard pour rien. */}
      {paniersAbandonnes &&
        paniersAbandonnes.incidents + paniersAbandonnes.abandons > 0 && (
          <ChartCard
            title="Paniers abandonnés au paiement"
            subtitle={resumeAbandons(paniersAbandonnes)}
          >
            <ul className="divide-y divide-gray-100">
              {/* Déjà trié par le serveur : l'argent pris d'abord. */}
              {paniersAbandonnes.paniers.slice(0, 8).map((p) => (
                <li
                  key={p.id}
                  className={`flex items-start justify-between gap-3 py-2 ${
                    p.argent_pris
                      ? '-mx-2 rounded border-l-4 border-red-500 bg-red-50 px-2'
                      : ''
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {p.client.nom}
                      <span className="ml-2 font-semibold">{centsToEur(p.montant_cents)}</span>
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {p.nb_lignes} article{p.nb_lignes > 1 ? 's' : ''} ·{' '}
                      {heureBelge(p.created_at)}
                      {p.nb_tentatives > 1
                        ? ` · ${p.nb_tentatives} tentatives de paiement`
                        : ''}
                      {p.date_retrait ? ` · retrait ${dateCourte(p.date_retrait)}` : ''}
                      {p.client.telephone
                        ? ` · ${p.client.telephone}`
                        : p.client.email
                        ? ` · ${p.client.email}`
                        : ''}
                    </p>
                    {/* L'argent prime à droite : si une commande existe malgré tout, elle se
                        dit ici plutôt que de disparaître derrière l'alerte. */}
                    {p.argent_pris && p.commande_suivante && (
                      <p className="text-xs text-gray-500 truncate">
                        Commandé {delaiLisible(p.commande_suivante.delai_minutes)} plus tard ·{' '}
                        {p.commande_suivante.order_number}
                      </p>
                    )}
                  </div>
                  {p.argent_pris ? (
                    <span className="shrink-0 rounded-full bg-red-600 px-2 py-1 text-xs font-semibold text-white">
                      {p.argent_encaisse_cents > 0
                        ? `${centsToEur(p.argent_encaisse_cents)} encaissés, aucune commande`
                        : 'Argent encaissé, aucune commande'}
                    </span>
                  ) : p.commande_suivante ? (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      Commandé {delaiLisible(p.commande_suivante.delai_minutes)} plus tard ·{' '}
                      {p.commande_suivante.order_number}
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                      Aucune commande depuis
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {paniersAbandonnes.paniers.length > 8 && (
              <p className="mt-2 text-xs text-gray-400">
                et {paniersAbandonnes.paniers.length - 8} client
                {paniersAbandonnes.paniers.length - 8 > 1 ? 's' : ''} de plus sur les 7 derniers
                jours
              </p>
            )}
          </ChartCard>
        )}

      {/* CA 7 jours + colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="CA des 7 derniers jours" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                tickFormatter={(v) => format(new Date(v), 'EEE dd', { locale: fr })}
                fontSize={12}
              />
              <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)}€`} fontSize={12} />
              <Tooltip
                formatter={(v: any) => eur(Number(v))}
                labelFormatter={(l) => format(new Date(String(l)), 'EEEE d MMMM', { locale: fr })}
              />
              <Bar dataKey="ca_eur" fill="#2d6a4f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top produits du jour">
          {topProducts.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune vente aujourd'hui.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topProducts.map((p, i) => (
                <li key={p.product_id} className="py-2 flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-400 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {p.product_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {p.category_name || '—'}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">
                    {eur(p.total_ca_eur || 0)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>

      {/* Dernières ventes + accès rapide */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Dernières ventes">
          {!kpis?.recent_sales?.length ? (
            <p className="text-sm text-gray-500">Aucune vente récente.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {kpis.recent_sales.slice(0, 6).map((s: any) => (
                <li key={s.id} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {centsToEur(s.total_cents)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {s.items_count} article{s.items_count > 1 ? 's' : ''}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {format(new Date(s.created_at), 'HH:mm')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>

        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Accès rapide</h3>
          </div>
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`flex flex-col items-center justify-center p-4 rounded-lg transition-colors ${l.color}`}
              >
                <span className="text-3xl mb-1">{l.icon}</span>
                <span className="text-sm font-medium text-gray-900">{l.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
